import React, { useState } from 'react';
import {
  Pickaxe,
  Search,
  Pause,
  Play,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Star,
  Zap,
} from 'lucide-react';
import type { AdminMinerListItem } from '@nova-mine/shared';

interface MinersViewProps {
  miners: AdminMinerListItem[];
  total: number;
  limit: number;
  offset: number;
  typeFilter: string;
  onTypeFilterChange: (t: string) => void;
  statusFilter: string;
  onStatusFilterChange: (s: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onPageChange: (offset: number) => void;
  onAction: (minerId: string, action: 'pause' | 'resume' | 'terminate', reason: string) => Promise<void>;
  isLoading: boolean;
}

export const MinersView: React.FC<MinersViewProps> = ({
  miners,
  total,
  limit,
  offset,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  searchQuery,
  onSearchChange,
  onPageChange,
  onAction,
  isLoading,
}) => {
  const [selectedMiner, setSelectedMiner] = useState<AdminMinerListItem | null>(null);
  const [actionType, setActionType] = useState<'pause' | 'resume' | 'terminate' | null>(null);
  const [actionReason, setActionReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  const handleOpenAction = (miner: AdminMinerListItem, act: 'pause' | 'resume' | 'terminate') => {
    setSelectedMiner(miner);
    setActionType(act);
    setActionReason('');
    setActionError(null);
  };

  const handleConfirmAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMiner || !actionType) return;
    if (actionReason.trim().length < 5) {
      setActionError('A mandatory reason of at least 5 characters is required for audit logs.');
      return;
    }

    try {
      setIsSubmitting(true);
      setActionError(null);
      await onAction(selectedMiner.id, actionType, actionReason.trim());
      setSelectedMiner(null);
      setActionType(null);
    } catch (err: unknown) {
      console.error('[Miner Action Failed]', err);
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Policy banner */}
      <div className="glass-panel p-4 rounded-2xl border border-purple-500/20 bg-purple-950/20 flex items-start space-x-3 text-xs text-purple-200">
        <ShieldCheck className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-white">Safe Execution Policy: </span>
          <span>
            When an active miner is administratively paused or terminated, all currently accrued claimable coins are automatically crystallized to the user's permanent coin balance with an immutable ledger entry. Historical earnings are never lost or silently altered.
          </span>
        </div>
      </div>

      {/* Main Table Panel */}
      <div className="glass-panel rounded-3xl border border-white/10 overflow-hidden">
        {/* Toolbar */}
        <div className="p-5 border-b border-white/5 flex flex-wrap items-center justify-between gap-4 bg-space-950/40">
          <div>
            <h3 className="text-base font-black text-white flex items-center space-x-2">
              <Pickaxe className="w-5 h-5 text-cyan-400" />
              <span>Fleet & Rig Operations</span>
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Individual miner instances, live accrual rates, and auditable status controls.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Type Filter */}
            <div className="flex bg-space-900 border border-white/10 rounded-xl p-1 text-xs font-bold">
              {['all', 'main', 'auto', 'stars'].map((t) => (
                <button
                  key={t}
                  onClick={() => onTypeFilterChange(t)}
                  className={`px-3 py-1.5 rounded-lg capitalize transition-all ${
                    typeFilter === t
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <div className="flex bg-space-900 border border-white/10 rounded-xl p-1 text-xs font-bold">
              {['all', 'active', 'paused', 'terminated'].map((s) => (
                <button
                  key={s}
                  onClick={() => onStatusFilterChange(s)}
                  className={`px-3 py-1.5 rounded-lg capitalize transition-all ${
                    statusFilter === s
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search user, ID, Telegram..."
                className="bg-space-900 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 w-56"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-space-900/90 border-b border-white/5 text-gray-400 font-bold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="px-5 py-3.5">Miner ID</th>
                <th className="px-5 py-3.5">User Owner</th>
                <th className="px-5 py-3.5">Rig Type</th>
                <th className="px-5 py-3.5">24h Hashrate</th>
                <th className="px-5 py-3.5">Live Accrual</th>
                <th className="px-5 py-3.5">Total Claimed</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Next 24h Cycle</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-gray-300">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                    <div className="animate-pulse space-y-2 max-w-sm mx-auto">
                      <div className="h-4 bg-space-800 rounded w-full" />
                      <div className="h-4 bg-space-850 rounded w-4/5 mx-auto" />
                    </div>
                  </td>
                </tr>
              ) : miners.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No miners match the selected criteria.
                  </td>
                </tr>
              ) : (
                miners.map((m) => {
                  const effectiveDaily = m.daily_rate * m.units;
                  return (
                    <tr key={m.id} className="hover:bg-white/[0.02] transition-colors">
                      {/* Miner ID */}
                      <td className="px-5 py-4 font-mono text-gray-400">
                        <span title={m.id}>{m.id.slice(0, 10)}...</span>
                      </td>

                      {/* User Owner */}
                      <td className="px-5 py-4 font-semibold text-white">
                        <div>{m.user_name}</div>
                        <div className="text-[11px] text-gray-400 font-normal font-mono">
                          TG: {m.user_telegram_id}
                        </div>
                      </td>

                      {/* Rig Type */}
                      <td className="px-5 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          m.miner_type === 'stars'
                            ? 'bg-amber-500/20 text-nova-gold border border-amber-500/30'
                            : m.miner_type === 'auto'
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                        }`}>
                          {m.miner_type} {m.units > 1 ? `(${m.units}x)` : ''}
                        </span>
                      </td>

                      {/* Daily Hashrate */}
                      <td className="px-5 py-4 font-mono font-bold text-gray-200">
                        +{effectiveDaily.toLocaleString()} / 24h
                      </td>

                      {/* Live Accrual */}
                      <td className="px-5 py-4 font-mono font-bold text-emerald-400">
                        +{m.accrued_coins.toLocaleString()} NOVA
                      </td>

                      {/* Total Claimed */}
                      <td className="px-5 py-4 font-mono text-gray-400">
                        {m.total_claimed.toLocaleString()} NOVA
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          m.status === 'active'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : m.status === 'paused'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {m.status}
                        </span>
                      </td>

                      {/* Next Milestone */}
                      <td className="px-5 py-4 text-[11px] text-gray-400 font-mono">
                        {new Date(m.next_milestone_ms).toLocaleTimeString()}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {m.status === 'active' && (
                            <button
                              onClick={() => handleOpenAction(m, 'pause')}
                              className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30 transition-colors"
                              title="Pause Miner"
                            >
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {m.status === 'paused' && (
                            <button
                              onClick={() => handleOpenAction(m, 'resume')}
                              className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors"
                              title="Resume Miner"
                            >
                              <Play className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {m.status !== 'terminated' && (
                            <button
                              onClick={() => handleOpenAction(m, 'terminate')}
                              className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30 transition-colors"
                              title="Terminate Miner"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t border-white/5 flex items-center justify-between text-xs text-gray-400 bg-space-950/40">
          <div>
            Showing {miners.length > 0 ? offset + 1 : 0} to {Math.min(offset + limit, total)} of {total.toLocaleString()} rigs
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(Math.max(offset - limit, 0))}
              disabled={offset === 0 || isLoading}
              className="p-2 rounded-xl bg-space-900 border border-white/10 text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-mono font-bold text-gray-200">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(offset + limit)}
              disabled={offset + limit >= total || isLoading}
              className="p-2 rounded-xl bg-space-900 border border-white/10 text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {selectedMiner && actionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="max-w-md w-full glass-panel-glow rounded-3xl p-6 border border-white/15 bg-space-950 shadow-2xl">
            <div className="flex items-center space-x-3 text-amber-400 mb-4">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-base font-bold text-white capitalize">
                Confirm Miner {actionType}
              </h3>
            </div>

            <p className="text-xs text-gray-300">
              You are about to <span className="font-bold uppercase text-white">{actionType}</span> miner{' '}
              <span className="font-mono font-bold text-cyan-400">{selectedMiner.id}</span> owned by{' '}
              <span className="font-bold text-white">{selectedMiner.user_name}</span>.
            </p>

            {selectedMiner.status === 'active' && selectedMiner.accrued_coins > 0 && (
              <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300">
                <span className="font-bold block">Safe Accrual Crystallization:</span>
                +{selectedMiner.accrued_coins.toLocaleString()} accrued coins will be automatically credited to the user's permanent balance prior to {actionType}.
              </div>
            )}

            {actionError && (
              <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                {actionError}
              </div>
            )}

            <form onSubmit={handleConfirmAction} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">
                  Mandatory Audit Reason (min 5 characters)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="e.g. User request / investigated suspicious activity / scheduled maintenance..."
                  required
                  rows={3}
                  className="w-full bg-space-900 border border-white/15 rounded-xl p-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMiner(null);
                    setActionType(null);
                  }}
                  className="flex-1 py-2.5 rounded-xl bg-space-900 border border-white/10 text-xs font-bold text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 text-black text-xs font-bold shadow-lg hover:brightness-110 flex items-center justify-center space-x-1.5"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Executing...</span>
                    </>
                  ) : (
                    <span>Confirm {actionType}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
